import { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import DomainCard from "../../../shared/DomainCard";
import { Grid } from "@mui/material";
import StudentsList from "../StudentsList";
import LoCard from "../LoCard";
import ClassSkeleton from "../ClassSkeleton";
import DataNotFound from "components/DataNotFound/index";
import Tooltip from "components/Tooltip";
import { PendingAssignmentIcon } from "components/SVGs";
import classNames from "classnames";
import StudentScoreCell from "./StudentScoreCell";
import {
  selectClass,
  selectDomain,
  selectSelectedSort,
  studentsSet,
  baselineReportSet,
  selectPagination,
  selectBaseLineReport,
  paginationSet,
  selectGrade,
  IDomainReport,
} from "features/classPerformance";
import {
  getStudentsInfo,
  paginatedOptions,
  sortArray,
  createReportRecords,
} from "utils/ReportsHelpers";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useTranslation } from "react-i18next";
import {
  IDomainDetailsEntity,
  IDomainReportEntity,
  IDomainsReportEntity,
  IStudentDomainEntity,
  useFetchBaselineReportQuery,
} from "services/reports";
import { Assessment } from "features/common/Interfaces";

const colors = [
  "rgb(68 183 253)",
  "rgb(90 107 199)",
  "rgb(134 83 203)",
  "rgb(201 97 206)",
];

const useStyles = makeStyles({
  root: {
    "& > :nth-of-type(2n)": {
      background: "rgb(248 248 248)",
    },
    "& > :nth-of-type(2n+1)": {
      background: "rgb(255 255 255)",
    },
  },
  domainColumn: {
    "& > :last-child": {
      borderLeft: "1px solid rgb(214 214 214)",
      "& > div > :first-of-type": {
        width: "calc(100% + 5px)",
      },
    },
  },
  loColumn: {
    "& > :last-child": {
      borderLeft: "1px solid rgb(214 214 214)",
    },
  },
});

function Baseline() {
  const { t } = useTranslation();
  const selectedClass = useAppSelector(selectClass);
  const selectedGrade = useAppSelector(selectGrade);
  const selectedDomain = useAppSelector(selectDomain);
  const selectedSort = useAppSelector(selectSelectedSort);
  const pagination = useAppSelector(selectPagination);
  const baselineReport = useAppSelector(selectBaseLineReport);
  const dispatch = useAppDispatch();
  const { startIndex, endIndex } = paginatedOptions(pagination);
  const [data, setData] = useState<IDomainReport[]>([]);
  const classes = useStyles();
  const allDomains = selectedDomain?.value === -1;
  const {
    data: baselineReportData,
    isLoading,
    isUninitialized,
    isFetching,
  } = useFetchBaselineReportQuery(
    {
      classId: selectedClass?.value as number,
      domainId: selectedDomain?.value as number,
    },
    {
      skip: !selectedClass?.value || !selectedDomain?.value,
    }
  );

  const renderCell = (value: number) => {
    let cell =
      value >= 0 ? (
        `${value}%`
      ) : (
        <Tooltip title={t("pending")}>
          <div>
            <PendingAssignmentIcon />
          </div>
        </Tooltip>
      );

    return cell;
  };
  useEffect(() => {
    if (baselineReportData?._statusCode === 200) {
      let students: IStudentDomainEntity[] = [];
      let domains: IDomainDetailsEntity[] = [];
      const studentsData = structuredClone(
        (baselineReportData._entity as IDomainReportEntity)
          ._studentDomainDetails ?? []
      );
      const domainsData = structuredClone(
        (baselineReportData._entity as IDomainsReportEntity)
          ._domainAssessmentReportRes ?? []
      );

      if (studentsData) {
        students = studentsData.sort(sortArray(selectedSort));
      }

      domains = domainsData?.filter((item) => item !== null);
      const studentsDomainData =
        domains?.length > 0 ? domains[0]._studentAssessmentReports : [];
      const studentsInfo = allDomains ? studentsDomainData : students;
      dispatch(studentsSet(getStudentsInfo(studentsInfo)));
      dispatch(
        baselineReportSet(
          createReportRecords(
            allDomains,
            domainsData,
            studentsData,
            Assessment.BASELINE
          )
        )
      );
    } else {
      dispatch(baselineReportSet([]));
    }
  }, [baselineReportData, selectedSort]);

  useEffect(() => {
    if (baselineReport?.length > 0) {
      if (allDomains && baselineReport[0]?.scores !== undefined)
        setData(baselineReport);
      if (!allDomains && baselineReport[0]?.students !== undefined)
        setData(baselineReport.slice(startIndex, endIndex));
    } else {
      setData([]);
    }
  }, [baselineReport, pagination.currentPage]);

  useEffect(() => {
    if (selectedDomain?.value !== -1) {
      const total = baselineReport?.length;
      const totalPages =
        total === 0 ? 1 : Math.ceil(total / pagination.pageSize!);
      dispatch(paginationSet({ ...pagination, totalPages: totalPages }));
    }
  }, [baselineReport]);

  const renderSkeleton = () => (
    <Grid item xs={12}>
      <ClassSkeleton />
    </Grid>
  );

  const renderStudentMarksCell = (
    student: { score: string; studentId: number },
    loId: string
  ) => {
    return (
      <StudentScoreCell
        student={student}
        loId={loId}
        gradeInfo={selectedGrade!}
        classInfo={selectedClass!}
      />
    );
  };

  return (
    <>
      <Grid
        container
        className={classNames(
          classes.root,
          !allDomains && data.length !== 5 ? classes.loColumn : false,
          (!isLoading || !isFetching) && allDomains && data.length < 4
            ? classes.domainColumn
            : false
        )}
        alignItems="stretch"
      >
        {isLoading || isUninitialized || isFetching
          ? renderSkeleton()
          : renderClassData(data, allDomains)}
      </Grid>
    </>
  );

  function renderClassData(classData: IDomainReport[], isAll: boolean) {
    const compToRender = (
      <>
        <Grid
          item
          sx={{ width: isAll ? "24%" : "35%", borderTopRightRadius: "2rem" }}
        >
          <StudentsList />
        </Grid>
        {classData?.map((columnData, index) => (
          <Grid
            item
            key={"key" + new Date().getTime() + columnData.id}
            sx={{
              width: isAll ? "19%" : "13%",
              borderTopLeftRadius: allDomains ? "2rem" : 0,
            }}
          >
            {isAll ? (
              <DomainCard
                data={columnData}
                color={colors[index]}
                renderParentCell={renderCell}
              />
            ) : (
              <LoCard
                data={columnData}
                renderStudentMarksCell={renderStudentMarksCell}
              />
            )}
          </Grid>
        ))}
      </>
    );

    const noData = (
      <Grid item xs={12}>
        <DataNotFound />
      </Grid>
    );

    return data?.length > 0
      ? compToRender
      : isLoading || isUninitialized
      ? null
      : noData;
  }
}

export default Baseline;
